import { useState, useRef, useContext, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../authContext';
import { msalInstance } from '../../msalConfig';
import './Login.css';
import { useTranslation } from 'react-i18next';
import AppVersion from '../../components/AppVersion';
import { useLocation } from 'react-router-dom';
import { getPostLoginDestination } from '../../utils/postLoginRedirect';
import { EXPLICIT_LOGOUT_FLAG } from '../../utils/logout';
import { authenticateWithMicrosoft, isUnauthorizedError } from '../../utils/microsoftLogin';

function Login() {
  const { setLevel, setIsManager, setAccess, setEmail, setDate, setSessionId, setAssistantList, user, setUser, count, accessToken, setAccessToken, setUserName,setPopupStatus, setUpdateId, isLogin, setIsLogin, setTermsAccepted, setDomainsAccess, setUserEmail } = useContext(AuthContext);
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [microsoftError, setMicrosoftError] = useState(location.state?.error || '')
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { t: tLogin } = useTranslation('login');
  const { t: tCommon } = useTranslation('common');
  const [isAnimating, setIsAnimating] = useState(false);
  const [sideBVisible, setSideBVisible] = useState(true);
  const [isSsoLoading, setIsSsoLoading] = useState(true);
  const { instance } = useMsal();

  const completeMicrosoftLogin = async (account) => {
    msalInstance.setActiveAccount(account);
    const msalEmail = account.username;
    setUserEmail(msalEmail);

    const domainsResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_domains`);
    const domainData = await domainsResponse.json();
    const allowedDomains = domainData.map(d => d.dominio);
    const emailDomain = msalEmail.split('@')[1];

    // Checagem de domínio no cliente: só UX. A decisão válida é a do servidor,
    // em /auth/microsoft (specs/002-microsoft-auth-idtoken).
    if (!allowedDomains.includes(emailDomain)) {
      setMicrosoftError(tLogin('domain') || 'Domínio não autorizado.');
      setIsSsoLoading(false);
      return;
    }

    // idToken do MSAL validado no servidor; sem segredo no cliente.
    let loginData;
    try {
      loginData = await authenticateWithMicrosoft(instance, account);
    } catch (authError) {
      if (isUnauthorizedError(authError)) {
        setMicrosoftError(tLogin('domain') || 'Domínio não autorizado.');
        setIsSsoLoading(false);
        return;
      }
      throw authError;
    }

    if (loginData.access_token) {
      setUser(msalEmail);
      setEmail(loginData.email);
      setLevel(loginData.role);
      setIsManager(Boolean(loginData.is_manager));
      setAccess(loginData.access);
      setDomainsAccess(loginData.domains_access || []);
      setAccessToken(loginData.access_token);
      setPopupStatus(loginData.popup_info.popup_status);
      setUpdateId(loginData.popup_info.update_id);
      setTermsAccepted(Boolean(loginData.terms_info?.term_status));
      setIsLogin(true);

      try {
        const graphResponse = await instance.acquireTokenSilent({ scopes: ["User.Read"], account });
        const userGraphResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
          headers: { Authorization: `Bearer ${graphResponse.accessToken}` }
        });
        const userData = await userGraphResponse.json();
        setUserName(userData.displayName);
      } catch {
        setUserName('');
      }

      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}:${String(currentDate.getSeconds()).padStart(2, '0')}.${String(currentDate.getMilliseconds()).padStart(6, '0')}`;
      setDate(formattedDate);
      setSessionId(`${msalEmail}-${count}-${formattedDate}`);
      setAssistantList(loginData.assistants || []);
      const destination = getPostLoginDestination(loginData.assistants, loginData.role);
      navigate(destination.path, { state: destination.state });
    }
  };

  // Tenta SSO silencioso ao montar a página
  useEffect(() => {
    const trySilentLogin = async () => {
      // Se o usuário acabou de fazer logout, não refaz o SSO silencioso —
      // senão ele seria relogado automaticamente. O flag é limpo quando o
      // usuário volta a logar por vontade própria.
      if (sessionStorage.getItem(EXPLICIT_LOGOUT_FLAG)) {
        setIsSsoLoading(false);
        return;
      }
      try {
        await msalInstance.handleRedirectPromise();
        const account = msalInstance.getActiveAccount();
        if (account) {
          await completeMicrosoftLogin(account);
          return;
        }
        const ssoResponse = await msalInstance.ssoSilent({ scopes: ["User.Read"] });
        await completeMicrosoftLogin(ssoResponse.account);
      } catch {
        // SSO silencioso falhou — mostra formulário normalmente
        setIsSsoLoading(false);
      }
    };

    trySilentLogin();
  }, []);

  // Login with Microsoft (botão manual)
  const handleMicrosoftLogin = async () => {
    try {
      sessionStorage.removeItem(EXPLICIT_LOGOUT_FLAG);
      navigate('/login');
      setIsAnimating(true);
    } catch (error) {
      console.error("Erro no loginRedirect:", error);
      setMicrosoftError(tLogin('microsoftRedirectError'));
    }
  };


  const handleLogin = async () => {
    if (!user || !password) {
      setError(tLogin('fillLoginAndPassword'));
      return;
    }
    setIsAnimating(true);
    setSideBVisible(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/post_login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: user, password: password }),
      });

      const data = await response.json();

      if (data.access_token) {
        sessionStorage.removeItem(EXPLICIT_LOGOUT_FLAG);
        setUser(data.email);
        setUserName(data.name);
        setLevel(data.role); // Atualiza o nível do usuário
        setIsManager(Boolean(data.is_manager));
        setAccess(data.access);
        setDomainsAccess(data.domains_access || []); // Atualiza os domínios de acesso do usuário
        setEmail(data.email);
        setAccessToken(data.access_token);
        setPopupStatus(data.popup_info.popup_status);
        setUpdateId(data.popup_info.update_id); // Configurar status do popup 

        if (data.popup_info.popup_status !== undefined) {
          setPopupStatus(data.popup_info.popup_status);
        }

        setIsLogin(true)
        setTermsAccepted(Boolean(data.terms_info?.term_status));
        // Captura a data e horário atual e formata para ISO 8601

        const currentDate = new Date();
        const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}:${String(currentDate.getSeconds()).padStart(2, '0')}.${String(currentDate.getMilliseconds()).padStart(6, '0')}`;

        setDate(formattedDate);
        setSessionId(`${data.email}-${count}-${formattedDate}`);

        // Define a lista de assistentes de acordo com os dados recebidos do backend
        if (data.assistants) {
          setAssistantList(data.assistants);
        } else {
          setAssistantList([]);
        }
        const destination = getPostLoginDestination(data.assistants, data.role);
        navigate(destination.path, { state: destination.state });
      } else {
        setIsAnimating(false);
        setError(tLogin('invalidLoginOrPassword'));
        setTimeout(() => {
          setSideBVisible(true);
        }, 700);
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      setError(tLogin('loginError'));
      setIsAnimating(false);
      setTimeout(() => {
        setSideBVisible(true);
      }, 700);
    }
  };

  const handleUsernameChange = (e) => {
    setUser(e.target.value);
    setError(''); // Limpa a mensagem de erro quando o usuário começa a digitar
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError(''); // Limpa a mensagem de erro quando o usuário começa a digitar
  };

  const handleUsernameKeyPress = (event) => {
    if (event.key === 'Enter') {
      passwordRef.current.focus(); // Foca no input de senha ao pressionar "Enter"
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword); // Alterna entre mostrar/ocultar senha
  };

  if (isSsoLoading) {
    return (
      <div className="login-container login-success">
        <div className="image-carousel">
          <img src="/imagens/linhas.svg" className="background-svg" alt="Background Lines" />
          <img src="/imagens/logo-e-titulo-tpf.svg" className="logo-svg" alt="Logo TPF" />
        </div>
        <AppVersion />
      </div>
    );
  }

  return (
    <div className={`login-container ${isAnimating ? 'login-success' : ''}`}>
      {/*SIDE A*/}
      <div className="image-carousel">
        <img src="/imagens/linhas.svg" className="background-svg" alt="Background Lines" />
        <img src="/imagens/logo-e-titulo-tpf.svg" className="logo-svg" alt="Logo TPF" />
      </div>

      {/*SIDE B*/}
      <div className={`sideb-container ${sideBVisible ? 'show' : 'hidden'}`}>

        <div className="form-container">
          <div className="centered-container">
            <img
              src="/imagens/iris-logo.svg"
              className="container-logo-iris"
              alt="Logo Iris"
            />

            <div className="welcome-container">
              <h3 className="welcome-title">{tLogin('welcome')}</h3>
            </div>

            <div className="forms">
              <div className="input-container-login">
                <input
                  type="text"
                  placeholder={tLogin('user-login')}
                  value={user}
                  onChange={handleUsernameChange}
                  onKeyDown={handleUsernameKeyPress} // Adiciona evento de pressionar tecla
                  className="input-field"
                  data-testid="input-usuario"
                />
                <img src="/imagens/e-mail_icon.svg" alt="Email Icon" className="input-icon" />
              </div>

              <div className="input-container-login">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={tLogin('password')}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  ref={passwordRef}
                  className="input-field"
                  data-testid="input-password"
                />
                <img src="/imagens/lock.svg" alt="Password Icon" className="input-icon" />
                <img
                  src={showPassword ? "/imagens/eye-open.svg" : "/imagens/eye-closed.svg"}
                  alt="Toggle Password Visibility"
                  className="toggle-password-icon"
                  onClick={handleTogglePassword}
                />
              </div>

              <div className="forgot-link-container" style={{ width: '100%', textAlign: 'end' }}>
                <button
                  type="button"
                  className="forgot-link-button"
                  onClick={() => navigate('/forgotPassword')}
                >
                  {tLogin('forgotPasswordTitle')}
                </button>
              </div>

              {/* Mensagem de erro */}
              {error && <p className="error-message">{error}</p>}

              <div>
                <button
                  type="submit"
                  className={`primary-button ${handleLogin ? 'disabled' : ''}`}
                  onClick={handleLogin}
                >
                  {tLogin('login')}
                </button>
              </div>


              <div className="separator">
                <div className="separator-line"></div>
                <div className="separator-text">{tCommon('or')}</div>
                <div className="separator-line"></div>
              </div>
              {microsoftError && <p className="microsoft-error-message">{microsoftError}</p>}
              <button className="outlook-login" onClick={handleMicrosoftLogin}>
                <div><img src="imagens/microsoft-icon.svg" alt="Microsoft Icon" className="outlook-icon" /></div>
                <div>{tLogin('LoginWithOutlook')}</div>
              </button>
            </div>
          </div>
        </div>

        <footer className='footer-login'></footer>

      </div>

      <AppVersion />
    </div>

  );
}

export default Login;
